import { calculateTradePrice } from "@dmf/calculator-library";
import { Badge, Button, Col, Form, Input, InputNumber, Modal, Popconfirm, Popover, Row, Select, Space, Switch, Table, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from 'moment';
import { Key, useEffect, useMemo, useRef, useState } from "react";
import Icon from "../../../../assets/icons/icon";
import DividerLine from "../../../../components/lineSplit";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import TaxCaseStore from "../../../../contexts/taxCaseStore";
import TaxFlowStore, { TaxFlow } from "../../../../contexts/taxFlowStore";
import _t from "../../../../lang/translate";
import { Vehicle } from "../../../../types/appTypes";
import { convertToApiFileList } from "../../../../utilities/convertTypes";
import { yearDiff } from "../../../../utilities/date";
import format from "../../../../utilities/formatNumbers";
import { mergeListsById } from "../../../../utilities/mergeLists";
import { isValidVin } from "../../../../utilities/stringExtraFunctions";
import { isValidNum } from "../../../../utilities/typeGuard";
import AdvertDetails from "../advert/advertDetails";
import createAdvertAsync from "../advert/createAdvert";
import openMarketPlace from "../advert/openMarketPlace";
import { Advert, EnrichmentCompletedPayload, EnrichmentMeta, Filters, TaxAdvert, TaxAdvertConfig } from "../advert/types";
import { applyEquipmentChangesToAdvert, exclusionReasons, initializeAdvertEquipment, initializeAdvertRegulation, internalEnrichmentFinishEvent, mapQuality } from "./util";



export default function TaxFlowAdverts() {
  const { taxFlow, setTaxFlow, setTaxFlowSilent } = TaxFlowStore.useContainer()
  const { taxCase } = TaxCaseStore.useContainer()
  const didMountRef = useRef(false);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (!taxFlow.newPrice?.manager) return;

    setTaxFlowSilent((prev) => ({
      ...prev,
      adverts: prev.adverts?.map(ad => {
        if (!ad.config?.enrichment) return ad;
        const newPriceManager = applyEquipmentChangesToAdvert(taxFlow, ad)
        return {
          ...ad,
          config: {
            ...ad.config,
            newPriceManager
          }
        }
      })
    }))
  }, [
    taxFlow?.newPrice?.manager?.data?.equipment.length,
    taxFlow?.newPrice?.manager?.meta?.fetchedKey,
    taxFlow?.newPrice?.manager?.data?.selectedNewPrice
  ])

  useEffect(() => {
    async function listener(e: Event) {
      const payload = (e as CustomEvent<EnrichmentCompletedPayload>).detail;

      const payloadMatchesAdvert = (item: TaxAdvert) => {
        const matchingEcodeContext = payload.eCodeContext === taxCase.vehicle?.motorAtlasId
        const matchingPriceDate = payload?.priceDate?.slice(0, 10) === taxCase.vehicle?.firstRegDate?.slice(0, 10)
        return item.id === Number(payload.advertId)
          && matchingEcodeContext
          && matchingPriceDate
      }

      const advertMatchedPayload = taxFlow.adverts?.some(payloadMatchesAdvert)
      if (!advertMatchedPayload) return;
      function mapEnrichmentPayload(ad: TaxAdvert): TaxAdvert {
        if (ad.id !== Number(payload.advertId)) return ad;
        const newPriceManager = initializeAdvertEquipment(taxFlow, payload)
        return {
          ...ad,
          eCode: payload.vinData?.eCode,
          config: {
            ...ad.config,
            enrichment: payload,
            enrichmentMeta: {
              ...ad.config?.enrichmentMeta,
              status: payload.completionStatus === 'success' ? "ready" : "failed",
              completedAt: new Date().toISOString(),
            },
            equipmentSource: "taxCase",
            newPriceManager,
          }
        }

      }
      const newAds = taxFlow.adverts?.map(mapEnrichmentPayload)
      setTaxFlow({ ...taxFlow, adverts: newAds })
    }
    window.addEventListener(internalEnrichmentFinishEvent, listener)
    return () => {
      window.removeEventListener(internalEnrichmentFinishEvent, listener)
    }
  }, [taxFlow.adverts, taxFlow.newPrice])

  return (
    <div>
      <IncludedTable />
      <DividerLine />
      <ExcludedTable />
    </div>
  )
}
type BestCombo = {
  r: number;
  bestTradePrice: number | null;
  bestCombination: TaxAdvert[];
}

function IncludedTable() {
  const { taxCase } = TaxCaseStore.useContainer()
  const { taxFlow, setTaxFlow, equipmentSums } = TaxFlowStore.useContainer()
  const [selectedIncluded, setSelectedIncluded] = useState<Key[]>([])
  const [settingsVisible, setSettingsVisible] = useState(false)
  const [selectedAdvert, setSelectedAdvert] = useState<TaxAdvert | null>(null)
  const [adDetailsVisible, setAdDetailsVisible] = useState(false)
  const [combinationSize, setCombinationSize] = useState<number | null>(3)
  const [bestCombo, setBestCombo] = useState<BestCombo | null>(null)
  const included = useMemo(() => mapAdvertToVM(taxFlow.adverts?.filter(ad => ad.config?.isIncluded) ?? []), [taxFlow])


  const includedTableColumns: ColumnsType<AdvertVM> = [
    ...columns,
    {
      width: 64,
      align: "right",
      render(value, record, index) {
        // Somehow detect that is has loaded for 20 seconds. I dont want constant rerenders
        // const isRetryable = canRetry(record.enrichmentMeta)
        return (
          <Row justify="space-between">
            <Col>
              <DisplayEnrichmentStatus record={record} newPrice={taxFlow.newPrice?.newPrice ?? null} vehicle={taxCase.vehicle} />
            </Col>
            <Col>
              <Button size="small" type="text" shape="circle"
                onClick={() => openDetails(record)}
              // disabled={isRetryable}
              >
                <Icon size="large" name="pencil-simple" />
              </Button>
            </Col>
          </Row>
        );
      },
    }
  ]
  function openDetails(record: AdvertVM) {
    const ad = taxFlow.adverts?.find(item => item.id === record.id)
    setSelectedAdvert(ad ?? null)
    setAdDetailsVisible(true)
  }
  function resetRowSelections() {
    setSelectedIncluded([])
  }
  function handleAdDetailsSubmit(advert: TaxAdvert) {
    setTaxFlow((prev) => {
      return {
        ...prev,
        adverts: prev.adverts?.map(item => {
          if (item.id !== advert.id) return item
          return {
            ...advert
          }
        })
      }
    })
  }

  function autoChoose() {
    try {
      if (!combinationSize) {
        throw new Error("Du skal angive en kombinationsstørrelse")
      }

      const bestCombo: BestCombo | null = getBestCombinationAcrossR(taxFlow.adverts?.filter(ad => ad.config?.isIncluded) ?? [], combinationSize, combinationSize)
      setBestCombo(bestCombo)
    } catch (error) {
      renderErrorMessage(error)
    }
  }

  function getBestCombinationAcrossR(adverts: TaxAdvert[], minR: number, maxR = adverts.length) {
    let overallBest: { r: number; bestTradePrice: number | null; bestCombination: TaxAdvert[] } | null = null;

    for (let r = minR; r <= maxR; r++) {
      const result = getBestCombination(adverts, r);
      if (
        result.bestTradePrice != null &&
        (overallBest?.bestTradePrice == null || result.bestTradePrice < overallBest.bestTradePrice)
      ) {
        overallBest = { r, ...result };
      }
    }

    return overallBest;
  }
  function getBestCombination(adverts: TaxAdvert[], r: number) {
    const indexToAdvert = adverts;
    let bestTradePrice: number | null = null;
    let bestCombination: number[] = [];

    forEachCombination(adverts.length, r, (combo) => {
      const selected = combo.map(i => indexToAdvert[i]);
      const input = buildTradePriceInput(selected);
      if (!input) return;

      const tradePrice = calculateTradePrice(input)?.result?.tradePrice;

      if (
        (!isValidNum(bestTradePrice) && isValidNum(tradePrice)) ||
        (isValidNum(tradePrice) && isValidNum(bestTradePrice) && tradePrice < bestTradePrice)
      ) {
        bestTradePrice = tradePrice;
        bestCombination = [...combo];
      }
    });

    return {
      bestTradePrice,
      bestCombination: bestCombination.map(i => indexToAdvert[i]),
    };
  }

  const calcDateRef = useRef(new Date().toISOString());
  function buildTradePriceInput(adverts: TaxAdvert[]) {
    const v = taxCase.vehicle;
    if (!v?.firstRegDate) return null;
    if (!adverts.length) return null;

    return {
      equipmentPrice: equipmentSums.evaluationVehicle ?? 0,
      firstRegDate: v.firstRegDate,
      mileage: v.mileage ?? 0,
      quality: mapQuality(v.qualityId ?? -1),
      specialUse: !!v.useCaseId && v.useCaseId !== 1,
      calcDate: calcDateRef.current,
      adverts: adverts.map(ad => ({
        equipmentPrice: equipmentSums[ad.id] ?? 0,
        firstRegDateYear: moment(ad.firstRegDate).year(),
        mileage: ad.mileage ?? 0,
        salesPrice: ad.price?.amount ?? 0,
      })),
      regulatedAdverts: adverts.map(ad => ({
        tradePriceLevel: ad.config?.regulations?.output?.tradePriceLevel ?? 0,
        ageDifference: moment(ad.firstRegDate).diff(v.firstRegDate, "year") ?? 0,
        ageRegulatedMileage: ad.config?.regulations?.output?.ageRegulatedMileage ?? 0,
        ageRegulatedMileageDifference: ad.config?.regulations?.output?.ageRegulatedMileageDifference ?? 0,
        ageRegulatedPrice: ad.config?.regulations?.output?.ageRegulatedPrice ?? 0,
        ageRegulatedPriceDifference: ad.config?.regulations?.output?.ageRegulatedPriceDifference ?? 0,
        unregulatedMileage: ad.mileage ?? 0,
        unregulatedPrice: ad.price?.amount ?? 0,
      })),
    };
  }

  const [shouldEnrich] = determineShouldEnrich(taxFlow.newPrice?.newPrice ?? null, taxCase.vehicle)
  return (
    <>
      {selectedAdvert && (
        <AdvertDetails enrichmentQuality={shouldEnrich ? "HIGH" : "LOW"} advert={selectedAdvert} visible={adDetailsVisible} setVisible={setAdDetailsVisible} submit={handleAdDetailsSubmit} />
      )}
      <ConfigureCaseAdverts visible={settingsVisible} setVisible={setSettingsVisible}
        selectedAdvertIds={selectedIncluded}
        resetRowSelections={resetRowSelections}
      />
      <Row justify="space-between" align="middle" className="mb-1">
        <Col>
          <h4 className="mb-0">Autovælg</h4>
        </Col>
        <Col>
          <Space>
            <span style={{ display: !!bestCombo ? "inline-block" : "none" }}>{format.price(bestCombo?.bestTradePrice)}</span>
            <Button style={{ display: !!bestCombo ? "inline-block" : "none" }}
              onClick={() => setBestCombo(null)} icon={<Icon name="repeat-outline" />} />
            <div>
              <InputNumber style={{ background: "white", width: 52, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                value={combinationSize} max={included?.length}
                onChange={(v) => setCombinationSize(v as number | null)}
              />
              <Button onClick={autoChoose}
                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                type="primary"
                icon={<Icon name="calculator-outline" />}
              />
            </div>
          </Space>
        </Col>
      </Row>



      <Table dataSource={included} columns={includedTableColumns}
        className="advert-management-table"
        rowSelection={{
          selectedRowKeys: selectedIncluded,
          onChange(selectedRowKeys, selectedRows) {
            setSelectedIncluded(selectedRowKeys)
          },
        }}
        onRow={(data) => {
          let className = ""
          if (bestCombo && !bestCombo?.bestCombination?.find(ad => data.id === ad.id)) {
            className += "highlight-danger"
          }

          return {
            className
          }
        }}
        size="small"
        rowKey={"id"}
        scroll={{ x: 1000 }}
        tableLayout="fixed"
        pagination={false}
        title={(data) => <IncludedAdvertTableActions setSettingsVisible={setSettingsVisible}
          records={data} selectedRowKeys={selectedIncluded} setSelectedRowKeys={setSelectedIncluded} resetRowSelections={() => setSelectedIncluded([])}
        />}
      />
    </>
  )
}

function checkAdNeedsData(record: AdvertVM, newPrice: number | null, vehicle: Vehicle | null): [boolean, string | null] {
  const hasMounted = !!record.enrichment?.mountedEquipment?.length;
  const hasVinEquipment = !!record.enrichment?.vinData?.equipment?.length;
  const hasAuthor = !!record.enrichment?.author;

  if (record.enrichmentMeta?.status === 'ready' && record.enrichmentMeta?.attemptCount === 0) {
    const [shouldEnrich, reasons] = determineShouldEnrich(newPrice, vehicle);
    return [true, reasons.join(", ")]
  }

  if (isValidVin(record.vin) && record.vin !== record.enrichment?.vinData?.vin) {
    return [true, "Stelnummer angivet af brugeren. Fejl kan forekomme."];
  }

  if (!record.enrichment?.vinData?.vin) {
    return [true, "Datamangel: Stelnummer mangler, så udstyrsdata kunne ikke hentes."];
  }

  if (hasAuthor) return [false, null];

  if (!hasMounted && !hasVinEquipment) {
    return [true, "Datamangel: Der findes hverken monteret udstyr eller standardudstyr."];
  }

  return [false, null];
}

function DisplayEnrichmentStatus({ record, vehicle, newPrice }: { record: AdvertVM, newPrice: number | null, vehicle: Vehicle | null }) {
  const s = record.enrichmentMeta?.status;
  if (s === 'failed') {
    return (
      <Tooltip title="Berigelse fejlede. Prøv igen, eller fjern annoncen hvis den hænger.">
        <Icon name="alert-outline" className="text-error" />
      </Tooltip>
    )
  }

  const [needsData, warningMessage] = checkAdNeedsData(record, newPrice, vehicle)
  if (s === 'ready' && needsData) {
    return (
      <Tooltip title={warningMessage}>
        <Icon name="warning-outline" size="large" className="text-warning" />
      </Tooltip>
    )
  }
  if (s === 'in_progress') {
    return (
      <Tooltip title={_t('msg.fetching_options')}>
        <Icon name="sync-outline" size="large" className="animate-spin" />
      </Tooltip>
    )
  }
  if (s === 'idle') {
    return (
      <Tooltip title={_t('pending', "actions")}>
        <Icon name="sync-outline" size="large" className="" />
      </Tooltip>
    )
  }
  return null
}

function ExcludedTable() {
  const { taxFlow, setTaxFlow } = TaxFlowStore.useContainer()
  const [selectedExcluded, setSelectedExcluded] = useState<Key[]>([])
  const [settingsVisible, setSettingsVisible] = useState(false)
  const excluded = useMemo(() => mapAdvertToVM(taxFlow.adverts?.filter(ad => !ad.config?.isIncluded) ?? []), [taxFlow.adverts])

  const excludedTableColumns: ColumnsType<AdvertVM> = useMemo(() => {
    return [
      ...columns,
      {
        title: _t("reason"),
        key: "exclusionReason",
        dataIndex: "exclusionReason",
        align: "right",
        ellipsis: true,
        width: 200,
        render(value, record, index) {
          return (
            <Tooltip overlayInnerStyle={{ whiteSpace: "pre" }} title={record.note}>
              <span>{value}</span>
            </Tooltip>
          )
        },
      }
    ]
  }, [])
  function resetRowSelections() {
    setSelectedExcluded([])
  }
  return (
    <>
      <ConfigureCaseAdverts visible={settingsVisible} setVisible={setSettingsVisible}
        selectedAdvertIds={selectedExcluded}
        resetRowSelections={resetRowSelections}
      />
      <Table dataSource={excluded} columns={excludedTableColumns}
        className="advert-management-table"
        rowSelection={{
          selectedRowKeys: selectedExcluded,
          onChange(selectedRowKeys, selectedRows) {
            setSelectedExcluded(selectedRowKeys)
          },
        }}
        size="small"
        rowKey={"id"}
        scroll={{ x: 1000 }}
        tableLayout="fixed"
        pagination={false}
        title={(data) => <ExcludedAdvertTableActions setSettingsVisible={setSettingsVisible}
          records={data} selectedRowKeys={selectedExcluded} resetRowSelections={() => setSelectedExcluded([])}
        />}
      />
    </>
  )

}


type AdvertTableActionsProps = {
  records: readonly AdvertVM[];
  selectedRowKeys: Key[];
  setSelectedRowKeys?: React.Dispatch<React.SetStateAction<Key[]>>
  resetRowSelections: () => void;
  setSettingsVisible: (v: boolean) => void;
}
function IncludedAdvertTableActions({ records, selectedRowKeys, setSelectedRowKeys, resetRowSelections, setSettingsVisible }: AdvertTableActionsProps) {
  const { taxFlow, setTaxFlow, taxScenario } = TaxFlowStore.useContainer()
  const { taxCase, setTaxCase } = TaxCaseStore.useContainer()
  const [loadingMarketplace, setLoadingMarketplace] = useState(false)
  const appliedFiltersRef = useRef<Filters | null>(null)

  async function handleAddAdvertManually() {
    try {
      const ad = await createAdvertAsync({ scenarioId: taxScenario?.id, taxCaseId: taxCase.id })
      if (!ad) return;

      const { regulationInput, regulationOutput } = initializeAdvertRegulation({
        firstRegDate: ad?.firstRegDate,
        salesPrice: ad?.price,
        evaluationVehicleFirstRegDate: taxCase.vehicle?.firstRegDate,
        mileage: ad?.mileage,
      })
      if (ad.documents?.length) {
        const scenarioKey = taxScenario?.flags?.optimization ? "optimization" : "offer"
        setTaxCase({
          ...taxCase,
          scenarios: {
            ...taxCase.scenarios,
            [scenarioKey]: {
              ...taxCase.scenarios[scenarioKey],
              files: convertToApiFileList(ad.documents)
            }
          }
        })
      }
      const newPriceManager = initializeAdvertEquipment(taxFlow, null)
      setTaxFlow((prev) => {
        return {
          ...prev,
          adverts: [...prev?.adverts ?? [], {
            id: Date.now(),
            horsePower: ad?.horsePower,
            firstRegDate: ad?.firstRegDate,
            type: ad?.type,
            manufacturer: ad?.make,
            model: ad?.model,
            variant: ad?.subModel,
            name: `${ad?.model ?? ''} ${ad?.subModel ?? ''} ${ad?.variant ?? ''}`,
            eCode: ad?.motorAtlasId,
            mileage: ad?.mileage,
            sourceName: ad?.source,
            seller: {
              name: ad?.sellerDealer,
            },
            price: {
              amount: ad?.price ?? null,
              currency: "DKK"
            },
            validFrom: moment().subtract(ad?.days, 'days').toISOString(),
            description: ad?.description,
            config: {
              isCustom: true,
              isIncluded: true,
              newPriceManager: newPriceManager,
              regulations: {
                input: regulationInput,
                output: regulationOutput
              }
            }
          }]
        }
      })
    } catch (error) {
      renderErrorMessage(error)
    }
  }



  async function handleOpenMarketPlace() {
    try {
      setLoadingMarketplace(true)
      const [shouldEnrich] = determineShouldEnrich(taxFlow.newPrice?.newPrice ?? null, taxCase.vehicle)

      const { advertSelections, appliedFilters, didStartEnrichment } = await openMarketPlace({
        preSelectedAdvertIds: records.map(ad => ad.id),
        vehicle: taxCase.vehicle,
        filters: appliedFiltersRef.current,
        enrichmentQuality: shouldEnrich ? "HIGH" : "LOW",
        newPriceWithoutEquipment: taxFlow.newPrice?.newPriceWithoutEquipment ?? null,
        equipmentPrice: taxFlow.newPrice?.equipmentPrice ?? null,
      })
      appliedFiltersRef.current = appliedFilters;

      const taxAdverts = mergeListsById(
        advertSelections?.map(ad => mapAdToTaxAd(ad, true, taxCase.vehicle?.firstRegDate, taxFlow, didStartEnrichment)) ?? [],
        taxFlow.adverts ?? [],
      )
      setTaxFlow((prev) => ({
        ...prev,
        adverts: taxAdverts ?? []
      }))
    } catch (error) {
      renderErrorMessage(false)
    } finally {
      setLoadingMarketplace(false)
    }
  }


  function deleteSelectedAdverts() {
    const idsToDelete = new Set(selectedRowKeys)
    setTaxFlow({ ...taxFlow, adverts: taxFlow.adverts?.filter(ad => !idsToDelete.has(ad.id)) })
    resetRowSelections()
  }
  function openCaseAdvertSettings() {
    setSettingsVisible(true)
  }

  return selectedRowKeys.length ? (
    <>
      <Row justify="space-between" gutter={[16, 16]} align="middle" wrap={true}>
        <Col>
          <Tag className="ant-tag-exception">{selectedRowKeys.length} {_t("chosen")}</Tag>
        </Col>
        <Col>
          <Space>
            <Button
              onClick={openCaseAdvertSettings}
              icon={<Icon name="pencil-simple" />}
            >
              Håndtering
            </Button>
            <Popconfirm title={_t("msg.confirm_delete")} onConfirm={deleteSelectedAdverts}>
              <Button danger>
                {_t("remove")}
              </Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>
    </>
  ) : (
    <Row justify="space-between" gutter={[16, 16]} align="middle" wrap={true}>
      <Col>
        <h4 className="mb-0">
          {_t("chosen", "adverts")} ({records.length} stk.)
        </h4>
      </Col>
      <Col>
        <Space>
          <Button onClick={handleAddAdvertManually}>{_t("addAdvertManually")}</Button>
          <Button onClick={handleOpenMarketPlace} type="primary" loading={loadingMarketplace}>{_t("findAdvertsOnMarket")}</Button>
        </Space>
      </Col>
    </Row>
  )
}
function ExcludedAdvertTableActions({ records, selectedRowKeys, resetRowSelections, setSettingsVisible }: AdvertTableActionsProps) {
  const { taxFlow, setTaxFlow } = TaxFlowStore.useContainer()

  function deleteSelectedAdverts() {
    const idsToDelete = new Set(selectedRowKeys)
    setTaxFlow({ ...taxFlow, adverts: taxFlow.adverts?.filter(ad => !idsToDelete.has(ad.id)) })
    resetRowSelections()
  }
  function moveToSelected() {
    const idsToMove = new Set(selectedRowKeys)
    setTaxFlow({
      ...taxFlow,
      adverts: taxFlow.adverts?.map(ad => {
        if (!idsToMove.has(ad.id)) return ad;
        return {
          ...ad,
          config: {
            ...ad.config,
            isIncluded: true,
            exclusionReason: null
          }
        }
      })
    })
    resetRowSelections()
  }
  return selectedRowKeys.length ? (
    <>
      <Row justify="space-between" align="middle" gutter={[16, 16]} wrap={true}>
        <Col>
          <Tag className="ant-tag-exception">{selectedRowKeys.length} {_t("chosen")}</Tag>
        </Col>
        <Col>
          <Space>
            <Button
              onClick={() => setSettingsVisible(true)}
              icon={<Icon name="pencil-simple" />}
            >
              Håndtering
            </Button>
            <Button
              onClick={moveToSelected}
              icon={<Icon name="arrow-up-outline" />}
            >
              Flyt til valgte
            </Button>
            <Popconfirm title={_t("msg.confirm_delete")} onConfirm={deleteSelectedAdverts}>
              <Button danger>
                {_t("remove")}
              </Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>
    </>
  ) : (
    <div style={{ height: 31, display: "flex", alignItems: "center" }}>
      <h4 className="mb-0">{_t("deselected", "adverts")} ({records.length} stk.)</h4>
    </div>
  )
}

function mapAdToTaxAd(ad: Advert, included: boolean, evaluationVehicleFirstRegDate: string | undefined | null, taxFlow: TaxFlow, didStartEnrichment: boolean): TaxAdvert {
  const originalPrice = ad.price
  const priceWithVat = ad.priceIncludesVat ? originalPrice?.amount ?? null : (originalPrice?.amount ?? 0) * 1.25;
  const { regulationInput, regulationOutput } = initializeAdvertRegulation({
    firstRegDate: ad.firstRegDate,
    salesPrice: priceWithVat,
    evaluationVehicleFirstRegDate,
    mileage: ad.mileage,
  })
  const startedAt = new Date().toISOString();
  const newPriceManager = initializeAdvertEquipment(taxFlow, null)

  return {
    ...ad,
    price: {
      ...ad.price,
      currency: ad.price?.currency ?? null,
      amount: ad.priceIncludesVat ? originalPrice?.amount ?? null : (originalPrice?.amount ?? 0) * 1.25,
    },
    config: {
      regulations: {
        input: regulationInput,
        output: regulationOutput
      },
      isIncluded: included,
      enrichmentMeta: {
        status: didStartEnrichment ? "in_progress" : "ready",
        attemptCount: didStartEnrichment ? 1 : 0,
        lastAttemptAt: didStartEnrichment ? startedAt : "",
        startedAt: didStartEnrichment ? startedAt : ""
      },
      equipmentSource: "taxCase",
      newPriceManager,
      originalPrice
    }
  }
}

type AdvertVM = {
  id: number;
  vin: string | null;
  eCode: string | null;
  description: string | null;
  idRender: string;
  isCustom: boolean;
  manufacturer: string | null | undefined;
  model: string | null | undefined;
  firstRegDate: string;
  mileage: string;
  engineSize: number | null | undefined;
  horsePower: number | null | undefined;
  price: string | null | undefined;
  equipmentPrice: string | null | undefined;
  validFrom: number;
  exclusionReason: string | undefined | null;
  note: string | undefined | null;
  enrichmentMeta: EnrichmentMeta | null | undefined;
  enrichment: EnrichmentCompletedPayload | null | undefined
  priceIncludesVat: boolean;
  originalPrice?: string;
}
function mapAdvertToVM(adverts: TaxAdvert[]): AdvertVM[] {
  return adverts.map(item => {
    return {
      id: item.id,
      vin: item.vin ?? item.vinOverride ?? null,
      eCode: item.eCode ?? null,
      description: item.description ?? null,
      idRender: item.config?.isCustom ? "Manuel" : item.id.toString(),
      isCustom: !!item.config?.isCustom,
      manufacturer: item.manufacturer,
      model: item.name,
      firstRegDate: format.date(item.firstRegDate),
      mileage: format.number(item.mileage),
      engineSize: item.engineSize,
      horsePower: item.horsePower,
      price: format.integer(item.price?.amount, { roundingMode: "ceil" }),
      equipmentPrice: format.number(item.config?.newPriceManager?.values.equipmentPrice),
      validFrom: moment().diff(moment(item.validFrom), 'days'),
      exclusionReason: item.config?.exclusionReason,
      note: item.config?.note,
      enrichmentMeta: item.config?.enrichmentMeta,
      enrichment: item.config?.enrichment,
      priceIncludesVat: item.priceIncludesVat ?? true,
      originalPrice: format.integer(item.config?.originalPrice?.amount)
    }
  })
}

const columns: ColumnsType<AdvertVM> = [
  {
    key: "idRender",
    dataIndex: "idRender",
    title: _t("id"),
    width: 64,
  },
  {
    key: "manufacturer",
    dataIndex: "manufacturer",
    title: _t("brand"),
    width: 120,
  },
  {
    key: "model",
    dataIndex: "model",
    title: _t("model"),
    ellipsis: true,
    render(value, record, index) {
      let title = "Annoncen har ingen stelnummer"
      let color: "error" | "warning" | "success" = "error"
      if (isValidVin(record.vin)) {
        color = "warning"
        title = record.vin
      }
      if (isValidVin(record.vin) && record.eCode) {
        color = "success"
        title += ", " + record.eCode
      }

      return (
        <div className="text-ellipsis" title={value}>
          <Tooltip title={title} >
            <Badge className="mr-03" status={color} />
          </Tooltip>
          {value}
        </div>
      )
    },
  },
  {
    key: "description",
    dataIndex: "description",
    title: (
      <Tooltip title={_t("description")}>
        <Icon size="large" name="information-circle-outline" />
      </Tooltip>
    ),
    width: 36,
    render(value, record, index) {
      return (
        <Popover title={_t("description")}
          overlayInnerStyle={{
            maxWidth: 600,
            maxHeight: 768,
            overflow: "auto"
          }}
          mouseEnterDelay={0}
          overlayClassName="instant-popover"
          placement="left"
          content={
            <p style={{ whiteSpace: "pre-line" }}>
              {value}
            </p>
          }>
          <Icon size="large" name="information-circle-outline" />
        </Popover>
      )
    },
  },
  {
    key: "firstRegDate",
    dataIndex: "firstRegDate",
    title: _t("firstRegDate"),
    width: 96,
  },
  {
    key: "mileage",
    dataIndex: "mileage",
    title: _t("mileage_unit"),
    width: 80,
  },
  {
    key: "horsePower",
    dataIndex: "horsePower",
    title: _t("horse_power"),
    width: 80,
  },
  {
    key: "price",
    dataIndex: "price",
    title: _t("price"),
    className: "text-bold",
    width: 80,
    render(value, record, index) {
      if (!record.priceIncludesVat) {
        return (
          <Tooltip placement="left" title={"ex moms " + record.originalPrice}>
            <span style={{ textDecoration: "underline dotted" }}>{value}</span>
          </Tooltip>
        )
      }
      return value
    },
  },
  {
    key: "equipmentPrice",
    dataIndex: "equipmentPrice",
    title: _t("equipment"),
    width: 80,
  },
  {
    key: "validFrom",
    dataIndex: "validFrom",
    title: _t("days"),
    width: 58,
  },
]

const getPriceLimit = (firstRegYear: number) => {
  switch (firstRegYear) {
    case 2021: return 411_420
    case 2022: return 408_747
    case 2023: return 408_447
    // case 2024: return 409_024 // 2024-jan
    case 2024: return 414_024 // 2024-feb
    case 2025: return 419_344
    case 2026: return 419_267
    default: return 419_267
  }
}
function determineShouldEnrich(newPrice: number | null, vehicle: Vehicle | null): [boolean, string[]] {
  // const priceLimit = getPriceLimit(new Date(vehicle?.firstRegDate ?? "").getFullYear());
  const priceLimit = getPriceLimit(new Date().getFullYear());
  const priceCheck = (newPrice ?? 0) > priceLimit
  const yearCheck = yearDiff(new Date(), vehicle?.firstRegDate ?? "") <= 8
  const shouldEnrich = priceCheck && yearCheck
  return [
    shouldEnrich,
    [
      priceCheck ? null : `Kan ikke beregies: Nyprisen er lavere end ${format.price(priceLimit)}`,
      yearCheck ? null : "Kan ikke beriges: Køretøjet er ældre end 8 år",
    ].filter(r => !!r) as string[]
  ]
}
function concatenateString(original: string, newStr: string) {
  if (!newStr) return original;
  return `${original}\r\n\r\n${newStr}`
}

type ConfigureCaseAdvertsProps = {
  visible: boolean;
  setVisible: (v: boolean) => void;
  selectedAdvertIds: Key[];
  resetRowSelections: () => void;
}
type AdvertSettings = Pick<TaxAdvertConfig, 'isIncluded' | 'exclusionReason' | 'note'>
function ConfigureCaseAdverts({ visible, setVisible, selectedAdvertIds, resetRowSelections }: ConfigureCaseAdvertsProps) {
  const [form] = Form.useForm()
  const { setTaxFlow, taxFlow } = TaxFlowStore.useContainer()
  const title = `Ændringer tilføjes til alle valgte annoncer (${selectedAdvertIds.length} stk.)`
  const initialValues = {}
  const handleSubmit = async () => {
    try {
      await form.validateFields([
        ['isIncluded'],
        ['exclusionReason'],
        ["note"]
      ])
      const values = form.getFieldsValue() as AdvertSettings
      const idsToTouch = new Set(selectedAdvertIds)
      const newAds: TaxAdvert[] = taxFlow.adverts?.map(item => {
        if (!idsToTouch.has(item.id)) return item;
        const reason = exclusionReasons?.find(item => item.value === values.exclusionReason)?.label?.toString()
        return {
          ...item,
          config: {
            ...item.config,
            isIncluded: !!values.isIncluded,
            note: concatenateString(item.config?.note ?? "", values.note ?? ""),
            exclusionReason: reason,
          }
        }
      }) ?? []
      setTaxFlow({ ...taxFlow, adverts: newAds })
      setVisible(false)
      resetRowSelections()
    } catch (error) {
      renderErrorMessage(error)
    }
  }
  function handleCancel() {
    setVisible(false)
    resetRowSelections()
  }
  return (
    <Modal title={title}
      onCancel={handleCancel}
      onOk={handleSubmit}
      visible={visible}
    >
      <Form form={form} labelAlign="left" initialValues={{ ...initialValues }} >
        <Form.Item name={["isIncluded"]} label={_t("included")} valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const included = getFieldValue('isIncluded')
            if (included) return null;

            return (
              <>
                <Form.Item name={["exclusionReason"]} label={_t("choose_reason/status")} rules={[{ required: true }]}>
                  <Select showSearch optionFilterProp="label" allowClear placeholder={_t("choose_reason")}
                    options={exclusionReasons?.toSorted(
                      (a, b) => a?.label?.toString()?.localeCompare(b?.label?.toString() ?? "") ?? 0
                    )}
                  />
                </Form.Item>

              </>
            )
          }}
        </Form.Item>
        <Form.Item name={["note"]} label={_t("comment")}>
          <Input.TextArea rows={4} placeholder="AMG-Line..." />
        </Form.Item>
      </Form>
    </Modal>
  )
}

function forEachCombination(n: number, r: number, callback: (combo: number[]) => void) {
  const combo = new Array<number>(r);

  function backtrack(start: number, depth: number) {
    if (depth === r) {
      callback([...combo]);
      return;
    }

    for (let i = start; i <= n - (r - depth); i++) {
      combo[depth] = i;
      backtrack(i + 1, depth + 1);
    }
  }

  backtrack(0, 0);
}


























